import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, defer, forkJoin, isObservable, Observable, ReplaySubject } from 'rxjs';
import { of } from 'rxjs';
import { catchError, concatMap, first, map, mergeMap, retry, shareReplay, take, tap } from 'rxjs/operators';
import { IEntity } from '../interfaces/entity';
import { DataService } from './data.service';
import { messageService } from './messages/message.module';
import { Type } from '@angular/core';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

export abstract class BaseRestService<T extends IEntity, T1> extends DataService<T>  {

  cache$: { [index: string]: Observable<any>; } = {};
  cacheTimeout = 3000;

  currentItem: T;
  shadowItem: T;  // a copy of the current item - allows for restore of changes to current item should we want to

  // notify a change in the collection of data to be displayed, returns the up to date collection for display
  onChanged: BehaviorSubject<any> = new BehaviorSubject([]);
  // notify a change to an individual item, returns the item
  onItemChanged: ReplaySubject<any> = new ReplaySubject<any>(1);

  onAfterSave: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  onSelected: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onDeleted: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onAdded: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onLoading: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  loadingTimer: any;

  // MRD - Note on Observables
  // We use BehaviourSubject where we want to keep track of the LAST value - useful for maintaining/populating lists
  // We use ReplaySubject where we want to keep track of one or more values
  // I am using ReplaySubject(1,100) - 1 = store only the last value, 100 = ms before the values stored in the ReplaySubject expires.
  // Thus ReplaySubject(1,100) is, in effect, a 'fire once' to all current subscribers - new subscribers will only get a value when a new value is added to the ReplaySubject
  // Tried using Subject instead of ReplaySubject but this doesn't seem to work for some reason :(

  // MRD - Note on http.get as an observable
  // http.get emits a value, then completes.  This means that we don't need pipe(take(1)) on subscribers to observables
  // derived from http.get as the source will complete after the first value anyway

  headers = new HttpHeaders();

  routeParams: any;
  filter: string;
  resourceUrl: string;
  constructor(protected type: Type<any>, protected http: HttpClient, protected baseResourceUrl: string, protected name: string, protected router: Router) {
    super(type, name, router);
    this.resourceUrl = '/' + baseResourceUrl;
  }

  itemChanged(val: any) {
    this.onItemChanged.next(val);
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, type?: string) {
    this.debug('resolve called');
    this.routeParams = route.params;
    if (this.routeParams.id) {
      return new Promise((resolve, reject) => {
        if (this.routeParams.id === 'new') {
          this.itemChanged(false);
          resolve(false);
        }
        else {
          const uid = this.routeParams.id;
          this.get(uid).pipe(take(1))
            .subscribe((response: any) => {
              this.currentItem = response;
              this.shadowItem = response;
              this.itemChanged(response);
              resolve(response);
            }, reject);
        }
      });
    }
    else {
      return new Promise<void>((resolve, reject) => {
        if (this.cacheEnabled !== true) {
          this.onChanged.next([]);
        }
        Promise.all([
          this.getAll(type),
        ]).then(
          ([data]) => {
            resolve();
          },
          reject,
        );
      });
    }
  }

  // MRD - helper function to add/fetch cached observables from observable cache
  cacheable(key: string, obs: Observable<any>, time: number = this.cacheTimeout, bufferReplays: number = 1): Observable<any> {
    if (!this.cache$[key]) {
      this.cache$[key] = this.addToObservableCache(key, obs, time, bufferReplays);
    }
    return this.cache$[key];
  }

  // add specified observable to observable cache
  addToObservableCache = (key: string, obs: Observable<any>, time: number = this.cacheTimeout, bufferReplays: number = 1): Observable<T[]> => this.cache$[key] = obs.pipe(
    shareReplay(bufferReplays, time), first(null, defer(() => this.addToObservableCache(key, obs, time, bufferReplays))), mergeMap((d) => (isObservable(d) ? d : of(d))))

  applyDataFilter(data: T[]): T[] {
    return data;
  }

  dataLoading(loading: boolean) {
    this.onLoading.next(loading);
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
    }
    if (loading) {
      this.loadingTimer = setTimeout(() => {
        this.dataLoading(false);
      }, 30000);
    }
    else {
      this.loadingTimer = undefined;
    }
  }

  // subscribes to getAllData and passes the result to subscribers via the onChanged subscription
  getAll(type?: string, urlParams?: string) {
    this.dataLoading(true);
    // have we cached any data from previous response for the request url?
    const getUrl = this.resourceUrl + ((type) ? ('/' + type) : '') + ((urlParams) ? ('?' + urlParams) : '');
    if (this.responseCache$[getUrl]) {
      // console.log('Cached response data:', this.responseCache$[getUrl]);
      this.onChanged.next(this.applyDataFilter(this.responseCache$[getUrl]));
      this.dataLoading(false);
    }
    // now get the latest data
    this.getAllData(type, urlParams).subscribe({
      next: (data) => {
        this.onChanged.next(this.applyDataFilter(data));
        this.dataLoading(false);
      },
      error: () => {
        this.dataLoading(false); this.onChanged.next([]);
      },
    });
  }

  // cold observable to return all data from cache or datastore
  getAllData(type?: string, urlParams?: string, forceRefresh?: boolean): Observable<T[]> {
    if (!forceRefresh) {
      // check for cached data...
      // we could use this.cache but this.getCache includes additional logging which helps with debug
      const cachedData = this.getCache();
      if (cachedData.length > 0) {
        // we have cached data - job done, return it as an observable
        return of(cachedData);
      }
    }

    // no data in the data cache, build the url we need to make the http get request
    // taking into account the type passed in the args to this function
    const getUrl = this.resourceUrl + ((type) ? ('/' + type) : '') + ((urlParams) ? ('?' + urlParams) : '');
    this.log('Resource url:' + getUrl);

    // build the observable and assign to a local variable so we can chain it later in this function
    // Note: we are using map to manipulate data and the http get will only be called when we subscribe to the chain of observables we will eventually return
    const http$ = this.http.get<T[]>(getUrl, httpOptions).pipe(
      retry({count:3, delay: 3000}),
      map((data: any) => {
        this.debug('Data received from API:', getUrl, data);
        data = data || [];

        // now we have data returned we need to collect together the observables we will execute later...
        const hydrate$ = [];

        // ...starting with an observable which will return the data we are, eventually, going to return
        hydrate$.push(of(data));

        // the 'hydrate' function returns on observable for each item in the data array
        data.map((item) => { hydrate$.push(this.hydrate(item)); });

        // return the collection of observables ready for our chain
        return hydrate$;
      }),
      catchError(this.handleError<T>(`Get ${(type || this.CacheName)}`, true)),
      // catchError(this.handleError<T>(`Get ${(plural(type || this.CacheName)).toLowerCase()}`, true)),
    );

    // now we have built our observable which returns a collection of observables we chain everything together...
    // concatMap takes the response from our http$ observable (outer observable created above) and passes it to our inner observable - forkJoin
    // the forkJoin takes the array of observables from the outer observable and executes them, returning the last emitted value from each as an array.
    // Because we added of(data) observable at the beginning of our array of observables then
    // the data we want to stash and return is in the first element of the array returned by forkJoin
    // Note: Again we are not actually subscribing to anything here so nothing will actually happen until we subscribe to the getAllData method
    // - ie getAllData is a cold observable
    return this.cacheable(getUrl, http$.pipe(take(1), concatMap((response: any[]) =>
      forkJoin(response).pipe(take(1), map((data) => {
        // stash the data we are interested in in local cache if caching enabled...
        this.setCache(getUrl, [...(data[0] as any)]);
        // return our data
        return data[0];
      }),
        catchError(this.handleError<T>(`Get ${(type || this.CacheName)} - hydration`, false)),
        // catchError(this.handleError<T>(`Get ${(plural(type || this.CacheName)).toLowerCase()} - hydration`, false)),
      ))));
  }

  // get one by guid id.  Will 404 if id not found
  get(uid: string): Observable<T> {
    
    // if uid not specified then no point continuing, return null
    if (!uid) { return of(null); }
    
    // check if matching record already in cache, if it is, return it
    const val = this.cache?.find((x) => (x.uid === uid));
    if (val) {
      return of(val);
    }
    else {
      // if got here then we need to retrieve the record from the data store
      const getUrl = `${this.resourceUrl}/${uid}`;
      this.debug('Get, url:' + getUrl);
      return this.http
        .get<T>(getUrl, httpOptions).pipe(
          take(1),
          retry({count:3, delay: 5000}),
          tap((response) => {
            this.log(`Fetched ${this.CacheName} uid=${uid}`);
            const data = response;
            if (response) {
              this.hydrate(data).pipe(take(1)).subscribe((item) => {
                // stash results in local cache, if enabled...
                this.updateOrAddToCache(item);
              });
            }
          }),
          map((response) => {
            return response;
          }),
          catchError(this.handleError<T>(`Get ${this.CacheName}, uid=${uid}`, true)),
        );
    }
  }

  // getPagedAll(sort: string, order: string, page: number): Observable<T1> {
  //   var res = this.http.get<T1>(this.resourceUrl);
  //   // .pipe(
  //   //   tap(_ => this.log('get paged all ${this.cacheName}')),
  //   //   catchError(this.handleError('getPagedAll ${this.cacheName}', [])),
  //   //   );
  //   // res.subscribe((response:any)=>{
  //   //     this.onChanged.next(response);
  //   //     this.onSelected.next(response[0]);
  //   //   });
  //   return res;
  // }

  // lookup record in cache by id
  getById(id: number, type?: string): Observable<T> {
    // check id - if zero or not specified then no point continuing, return null
    if (!id) { return of(null); }

    // check cache - if we have a matching record then no need to call getAllData
    const val = this.cache?.find((x) => (x.id === id));
    if (val) { 
      return of(val); 
    }
    else {
      // not found a matching record, try getAllData
      return this.getAllData(type).pipe(map((data: T[]) => {
        const val = data.find((x) => (x.id === id));
        return val || null;
      }));
    }
  }

  save(entity: T, successMessage?: string) {
    // if entity already exists then id will be set - put the entity to update the data store
    // if no id specified must be new entity, post to data store
    const http$ = (entity.id) ? this.put(entity, successMessage) : this.post(entity, successMessage);
    http$.subscribe((response) => {
      this.afterSave(response);
      this.onAfterSave.next(response);
    });
  }

  // save new or existing entity
  saveAsObservable(entity: T, successMessage?: string) {
    // if entity already exists then id will be set - put the entity to update the data store
    // if no id specified must be new entity, post to data store
    const http$ = (entity.id) ? this.put(entity, successMessage) : this.post(entity, successMessage);
    return http$.pipe(take(1), tap((response)=>{
      this.afterSave(response);
      this.onAfterSave.next(response);
    }));    
  }

  // delete the entity from the data store
  delete(entity: T | string | number, resetCache?: boolean): Observable<T> {
    const uid: string = ((typeof entity === 'string') || (typeof entity === 'number')) ? entity.toString() : entity.uid;
    const url = this.resourceUrl + '/' + uid;
    const resetcache = resetCache;

    // Call WebAPI to delete the database record. The API will either return the original object or an http 460 if the delete or wasn't allowed
    this.debug('Delete url:', url);
    return this.http.delete<T>(url, httpOptions).pipe(
      take(1),
      tap((_) => {

        this.log(`Deleted ${this.CacheName}, id=${uid}`);

        // if a delete has removed multiple records in the backend, reset the cache
        if (resetcache) {
          this.resetAllCache();
        }
        else {
          // Remove from local cache
          this.removeItemFromCache(uid);
        }

        // notify subscribers the item has been deleted
        this.onDeleted.next(entity);

        // notify subscribers of the change to data
        const currentVal = this.onChanged.getValue();
        const val = (currentVal && (currentVal.length > 0)) ? currentVal : [];
        const index = val.findIndex((x) => (x.uid === uid) || (x.id && (x.id.toString() === uid)));
        if (index > -1) {
          val.splice(index, 1);
        }
        this.onChanged.next(val);

      }),
      catchError(this.handleError<T>(`Delete ${this.CacheName}`, true)),
    );
  }

  // **Helper functions**//

  // Add new entity
  post(entity: T, successMessage?: string): Observable<T> {
    this.debug('Posting:', entity);
    try { this.debug('Entity to post:', JSON.stringify(entity)); } catch (e) { }
    this.debug('Resource Url:', this.resourceUrl);
    return this.http
      .post<T>(this.resourceUrl, entity, httpOptions).pipe(
        take(1),
        tap((savedEntity: T) => {
          this.log(`Added ${this.CacheName} w/ id=${savedEntity.id}`);

          // notify subscribers the item has been added
          this.onAdded.next(savedEntity);

          // hydrate item
          this.hydrate(savedEntity).pipe(take(1)).subscribe((hydratedEntity) => {
            this.updateAndNotifyItemChanged(hydratedEntity, successMessage);
            return hydratedEntity;
          });
        }),
        catchError(this.handleError<T>(`Add ${this.CacheName}`, true)));
  }

  // Update existing entity
  put(entity: T, successMessage?: string): Observable<T> {
    this.debug('Updating:', entity);
    try { this.debug('Entity to update:', JSON.stringify(entity)); } catch (e) { }
    const url = `${this.resourceUrl}/${entity.uid}`;
    this.debug('Resource Url:', url);
    return this.http.put<T>(url, entity, httpOptions).pipe(
      take(1),
      tap((savedEntity: T) => {
        this.log(`Updated ${this.CacheName}`);

        // hydrate item, if required
        this.hydrate(savedEntity).pipe(take(1)).subscribe((hydratedEntity) => {
          // update and notify subscribers of change
          this.updateAndNotifyItemChanged(hydratedEntity, successMessage);
          return hydratedEntity;
        });
      }),
      catchError(this.handleError<T>(`Update ${this.CacheName}`, true)));
  }

  updateAndNotifyItemChanged(entity: T, successMessage?: string) {
    const currentVal = this.onChanged.getValue();
    const val = (currentVal && (currentVal.length > 0)) ? currentVal : [];
    const index = val.findIndex((x) => x.uid === entity.uid);

    if (index > -1) {
      // item alredy exists, splice in the update
      val.splice(index, 1, entity);
    }
    else {
      // item doesn't exist, push it on to the end
      val.push(entity);
    }

    // cache changes, if cache enabled...
    this.updateOrAddToCache(entity);

    // make sure that any subscribers are notified about the change
    this.onChanged.next(val);
    this.itemChanged(entity);

    // notify the user of success
    if (successMessage) {
      messageService.success(successMessage);
    }
  }

  afterSave(entity) {
    //
  }

  hydrate(item: T): Observable<T> {
    // do nothing - override in descendants
    return of(item);
  }

  addUrlParam(currentParams: string, paramName: string, paramValue: string): string {
    let params = [];
    const s = `${paramName}=${paramValue}`;

    if (currentParams) {
      params = currentParams.split('&');
    }
    const index = params.findIndex((i) => i.indexOf(paramName) > -1);
    if (index > -1) {
      params[index] = s;
    }
    else {
      params.push(s);
    }
    return params.join('&');
  }

  clearCache() {
    super.clearCache();
    this.cache$ = {};
  }

  // helper method - clear cache and refetch data from datastore
  refresh(type?: string, urlParams?: string): Observable<T[]> {
    this.clearCache();
    return this.getAllData(type, urlParams);
  }

  public getUid(id: number): string {
    const item = this.getCache().find((i) => id === i.id);
    return (item != null) ? item.uid : undefined;
  }
}
